import { Link, NavLink, useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import { useEffect, useState } from "react";
import { LogOut, Search, ListVideo, User, Home, Sparkles, ChevronDown, UserCog, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProfile } from "@/context/ProfileContext";
import ProfileAvatar from "@/components/ProfileAvatar";
import { studioBranding } from "@/lib/studioBranding";
import { useDocumentBranding } from "@/lib/useDocumentBranding";

export default function StorefrontLayout({ tenant, children, currentUser, onLogout, showSubscriberNav = false, heroBleed = false }) {
  const navigate = useNavigate();
  const { sp } = useStore();
  const themeColor = tenant?.theme_color || "#7C3AED";
  const accentColor = tenant?.accent_color || "#F59E0B";
  const brand = studioBranding(tenant?.slug);
  const logoSrc = tenant?.logo_url || brand?.logoUrl || brand?.iconUrl || null;
  useDocumentBranding(tenant);
  const { activeProfile, clearActiveProfile } = useProfile();

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLink = ({ isActive }) =>
    `text-xs uppercase tracking-[0.25em] px-3 py-2 transition-colors ${isActive ? "text-white" : "text-white/55 hover:text-white"}`;

  const handleSwitchProfile = () => {
    clearActiveProfile();
    navigate(sp(`/select-profile`));
  };
  const handleLogout = () => {
    clearActiveProfile();
    onLogout?.();
  };

  return (
    <div
      className="min-h-screen bg-[#0a0a0a] text-white overflow-x-clip"
      data-testid="storefront-layout"
      style={{ "--tenant-theme": themeColor, "--tenant-accent": accentColor }}
    >
      <header className={`fixed top-0 inset-x-0 z-30 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-black/85 border-b border-white/10" : "bg-gradient-to-b from-black/65 via-black/25 to-transparent"}`} data-testid="storefront-header">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to={showSubscriberNav ? sp(`/home`) : sp(``)}
            className="flex items-center gap-3"
            data-testid="storefront-brand-link"
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={tenant?.name}
                className="h-11 md:h-12 w-auto max-w-[240px] object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <>
                <div
                  className="h-8 w-8 rounded-sm grid place-items-center text-[10px] font-bold tracking-widest"
                  style={{ backgroundColor: themeColor }}
                >
                  {(tenant?.name || "X").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex flex-col leading-none">
                  <span className="font-display font-bold text-base tracking-tight">{tenant?.name}</span>
                  {!tenant?.white_label_enabled && (
                    <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Powered by Vawworld</span>
                  )}
                </div>
              </>
            )}
          </Link>

          {showSubscriberNav && tenant?.slug && (
            <nav className="hidden md:flex items-center gap-1" data-testid="subscriber-nav">
              <NavLink to={sp(`/home`)} end className={navLink} data-testid="nav-home">
                <span className="inline-flex items-center gap-1.5"><Home className="w-3.5 h-3.5" /> Home</span>
              </NavLink>
              <NavLink to={sp(`/reels`)} className={navLink} data-testid="nav-reels">
                <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Reels</span>
              </NavLink>
              <NavLink to={sp(`/my-list`)} className={navLink} data-testid="nav-my-list">
                <span className="inline-flex items-center gap-1.5"><ListVideo className="w-3.5 h-3.5" /> My list</span>
              </NavLink>
              <NavLink to={sp(`/search`)} className={navLink} data-testid="nav-search">
                <span className="inline-flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Search</span>
              </NavLink>
              <NavLink to={sp(`/account`)} className={navLink} data-testid="nav-account">
                <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Account</span>
              </NavLink>
            </nav>
          )}

          <div className="flex items-center gap-3">
            {currentUser ? (
              activeProfile ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-2 px-2 h-10 hover:bg-white/5"
                      data-testid="storefront-profile-dropdown-trigger"
                    >
                      <ProfileAvatar profile={activeProfile} size="sm" />
                      <span className="text-xs hidden md:inline" data-testid="storefront-active-profile-name">
                        {activeProfile.name}
                      </span>
                      {activeProfile.is_kid && (
                        <span className="hidden md:inline text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 border border-white/20 text-white/65" data-testid="storefront-kid-badge">
                          Kid
                        </span>
                      )}
                      <ChevronDown className="w-3.5 h-3.5 text-white/55" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-[#0a0a0a] border border-white/10 text-white rounded-none min-w-[200px]"
                    data-testid="storefront-profile-dropdown"
                  >
                    <div className="px-3 py-3 flex items-center gap-2 border-b border-white/10">
                      <ProfileAvatar profile={activeProfile} size="md" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{activeProfile.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 truncate">{currentUser.email}</div>
                      </div>
                    </div>
                    <DropdownMenuItem
                      onClick={handleSwitchProfile}
                      className="text-xs uppercase tracking-[0.25em] cursor-pointer focus:bg-white/10"
                      data-testid="storefront-switch-profile"
                    >
                      <Users className="w-3.5 h-3.5 mr-2" /> Switch profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate(sp(`/profiles`))}
                      className="text-xs uppercase tracking-[0.25em] cursor-pointer focus:bg-white/10"
                      data-testid="storefront-manage-profiles"
                    >
                      <UserCog className="w-3.5 h-3.5 mr-2" /> Manage profiles
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-xs uppercase tracking-[0.25em] cursor-pointer focus:bg-white/10 text-rose-300"
                      data-testid="storefront-logout-button"
                    >
                      <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <span className="text-xs text-white/50 hidden lg:inline" data-testid="storefront-user-email">
                    {currentUser.email}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white/70 hover:text-white rounded-none h-8"
                    onClick={handleLogout}
                    data-testid="storefront-logout-button"
                  >
                    <LogOut className="w-3.5 h-3.5 mr-1.5" />
                    <span className="text-xs uppercase tracking-widest">Sign out</span>
                  </Button>
                </>
              )
            ) : (
              <>
                <button
                  onClick={() => navigate(sp(`/login`))}
                  className="text-xs uppercase tracking-[0.25em] text-white/70 hover:text-white px-3 py-2"
                  data-testid="storefront-nav-signin"
                >
                  Sign in
                </button>
                <button
                  onClick={() => navigate(sp(`/signup`))}
                  className="text-xs uppercase tracking-[0.25em] px-4 py-2 rounded-none transition-transform hover:-translate-y-0.5"
                  style={{ backgroundColor: themeColor, color: "#0a0a0a" }}
                  data-testid="storefront-nav-signup"
                >
                  Subscribe
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className={heroBleed ? "" : "pt-16"}>{children}</main>
      <Footer tenant={tenant} accentColor={accentColor} />
    </div>
  );
}

function Footer({ tenant, accentColor }) {
  const isWhiteLabel = tenant?.white_label_enabled;
  return (
    <footer className="border-t border-white/5 mt-24" data-testid="storefront-footer">
      <div className="max-w-[1400px] mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
        <span className="text-white/40">
          © {new Date().getFullYear()} {tenant?.name || "Studio"}
        </span>
        {!isWhiteLabel ? (
          <a
            href="https://vawworld.tv"
            target="_blank"
            rel="noreferrer"
            className="text-white/50 hover:text-white inline-flex items-center gap-2 uppercase tracking-[0.3em]"
            data-testid="footer-powered-by"
          >
            Powered by <span className="font-display font-bold text-white">Vawworld</span>
            <span className="w-1 h-1 rounded-full" style={{ backgroundColor: accentColor }} />
          </a>
        ) : (
          <span className="uppercase tracking-[0.3em] text-white/30" data-testid="footer-white-label">
            Independent studio
          </span>
        )}
      </div>
    </footer>
  );
}
